import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Box, Flex, Header, Paper } from '@driftt/dds'

import { Metadata } from 'components/Metadata'
import SavedViewsSidebar from 'components/SavedViewsSidebar'
import SaveFilterModal from 'components/SaveFilterModal'
import { driftLoadingStateBuilder } from 'components/States'
import { PresenceContext } from 'context/presenceContext'
import { ScrollContext } from 'context/scrollContext'
import useNotifications from 'hooks/useNotifications'
import usePresenceStore from 'hooks/usePresenceStore'
import { useAppSelector } from 'hooks/useRedux'
import useSavedFilterEditModal from 'hooks/useSavedFilterEditModal'
import useSavedFilters, { INACTIVE_FILTER } from 'hooks/useSavedFilters'
import { SessionMetrics } from 'hooks/useSessionMetrics'
import CCRMFeed from 'sections/CCRM/Feed'
import CCRMFeedbackModal from 'sections/CCRM/FeedbackModal'
import CCRMHeaderBar from 'sections/CCRM/HeaderBar'
import { segmentTrack, TrackType } from 'utils/tracking'

const ENGAGED_SESSIONS_KEY = 'ccrm.feed.engagedSessions'
const ENGAGED_SESSION_SCROLL_THRESHOLD = 500
const ENGAGED_SESSION_COUNT_FEEDBACK_THRESHOLD = 3

interface CCRMActivityFeedPageProps {
  sessionMetrics: SessionMetrics
}

const CCRMActivityFeedPage = ({ sessionMetrics }: CCRMActivityFeedPageProps) => {
  const presence = usePresenceStore()
  const { info } = useNotifications()

  const [hasSavedEngagedSession, setHasSavedEnagagedSession] = useState<boolean>(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false)

  const { activityScrollOffset, setActivityScrollOffset } = useContext(ScrollContext)
  const { loaded: filtersLoaded, privateFilters, sharedFilters } = useAppSelector(
    ({ filters }) => filters
  )
  const LoadingComponent = useMemo(() => driftLoadingStateBuilder('Loading'), [])

  const {
    activeSavedFilter,
    defaultFilterId,
    updateActiveFilterId,
    updateDefaultFilterId,
    removeSavedFilter
  } = useSavedFilters()

  const {
    showSavedFilterModal,
    editableFilterGroups,
    editableSavedFilter,
    onOpenNewModal,
    onOpenEditModal,
    onCloseModal
  } = useSavedFilterEditModal()

  useEffect(() => {
    segmentTrack(TrackType.ACTIVITY_PAGE_ACCESSED)
  }, [])

  const onScroll = useCallback(
    (scrollOffset: number) => {
      if (scrollOffset > ENGAGED_SESSION_SCROLL_THRESHOLD && !hasSavedEngagedSession) {
        const previousEngagedSessions = parseInt(localStorage.getItem(ENGAGED_SESSIONS_KEY) || '0')
        localStorage.setItem(ENGAGED_SESSIONS_KEY, (previousEngagedSessions + 1).toString())
        setHasSavedEnagagedSession(true)

        if (previousEngagedSessions + 1 === ENGAGED_SESSION_COUNT_FEEDBACK_THRESHOLD) {
          setShowFeedbackModal(true)
        }
      }
      setActivityScrollOffset(scrollOffset)
    },
    [
      setShowFeedbackModal,
      hasSavedEngagedSession,
      setHasSavedEnagagedSession,
      setActivityScrollOffset
    ]
  )

  if (!filtersLoaded) {
    return <LoadingComponent />
  }

  return (
    <PresenceContext.Provider value={presence}>
      <Metadata title="Drift | Account activity" />
      <Box css={{height: '100vh'}}>
        {showFeedbackModal && (
          <CCRMFeedbackModal
            onClose={() => setShowFeedbackModal(false)}
            onSubmit={() =>
              info('Feedback submitted - thanks for taking the time to make Drift better!')
            }
          />
        )}
        {showSavedFilterModal && (
          <SaveFilterModal
            editableGroups={editableFilterGroups}
            savedFilter={editableSavedFilter}
            onClose={onCloseModal}
          />
        )}
        <Flex fullHeight>
          <Paper 
            elevation={2}
            css={{
              minWidth: '200px', 
              height: '100%',
              padding: '32px 0px 32px 16px',
              overflowY: 'auto'}}>
            <Flex direction='column'>
              <SavedViewsSidebar
                filtersLoading={!filtersLoaded}
                activeFilterId={activeSavedFilter?.id || INACTIVE_FILTER}
                defaultFilterId={defaultFilterId}
                setActiveFilterId={updateActiveFilterId}
                updateDefaultFilterId={updateDefaultFilterId}
                removeSavedFilter={removeSavedFilter}
                onOpenEditModal={onOpenEditModal}
                onCreateSavedFilter={onOpenNewModal}
                onShowFeedbackModal={() => setShowFeedbackModal(true)}
                sharedFilters={sharedFilters}
                privateFilters={privateFilters}
              />
            </Flex>
          </Paper>
          <Flex 
            direction='column' 
            alignItems='center' 
            css={{
              width: '100%', 
              height: '100%',
              margin: '32px 32px 16px 32px',
            }}
          >
            <Flex.Item alignSelf='start' css={{marginBottom: '16px'}}>
              <Header>Account activity</Header>
            </Flex.Item>
            <CCRMHeaderBar
                activeFilter={activeSavedFilter}
                setActiveFilterId={updateActiveFilterId}
                onOpenEditModal={onOpenEditModal}
              />
            <Flex
              direction='column'
              alignItems='center'
              css={{ width: '100%', height: '100%', overflowY: 'scroll'}}
              id='feedContainer'>
              <CCRMFeed
                scrollTargetId="feedContainer"
                withRefresh
                onScroll={onScroll}
                sessionMetrics={sessionMetrics}
                initialScrollY={activityScrollOffset}
              />
            </Flex>
            
          </Flex>
        </Flex>

        
        
      </Box>
    </PresenceContext.Provider>
  )
}

export default CCRMActivityFeedPage
